import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Megaphone, Clock, Send, ToggleLeft, ToggleRight, Upload, FileSpreadsheet, CheckCircle2, Mail, Bell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

import { ProductSelector } from '../components/admin/ordercalls/ProductSelector';
import { ClientSelector } from '../components/admin/ordercalls/ClientSelector';
import { OrderCallHistory } from '../components/admin/ordercalls/OrderCallHistory';

export default function AdminOrderCalls() {
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [history, setHistory] = useState([]);

    const [products, setProducts] = useState([]);
    const [clients, setClients] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [clientSearch, setClientSearch] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedClients, setSelectedClients] = useState([]);
    const [deadline, setDeadline] = useState('');
    const [deadlineTime, setDeadlineTime] = useState('18:00');
    const [message, setMessage] = useState('');
    const [sendEmail, setSendEmail] = useState(true);
    const [showProductList, setShowProductList] = useState(false);
    const [showClientList, setShowClientList] = useState(false);
    const [expandedCall, setExpandedCall] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    const loadSettings = useCallback(async () => {
        try { const res = await api.get('/admin/order-calls/settings'); setEnabled(res.data.enabled); }
        catch (err) { console.error('Error loading order call settings:', err); }
    }, []);

    const loadHistory = useCallback(async () => {
        try { const res = await api.get('/admin/order-calls'); setHistory(res.data.order_calls || []); }
        catch (err) { console.error('Error loading order call history:', err); }
    }, []);

    const loadProducts = useCallback(async () => {
        try { const res = await api.get('/products?limit=2000'); setProducts(res.data.products || []); }
        catch (err) { console.error('Error loading products:', err); }
    }, []);

    const loadClients = useCallback(async () => {
        try { const res = await api.get('/admin/clients?limit=2000'); setClients(res.data.clients || []); }
        catch (err) { console.error('Error loading clients:', err); }
    }, []);

    useEffect(() => {
        Promise.all([loadSettings(), loadHistory(), loadProducts(), loadClients()])
            .finally(() => setLoading(false));
    }, [loadSettings, loadHistory, loadProducts, loadClients]);

    const toggleEnabled = async () => {
        try {
            const res = await api.put('/admin/order-calls/settings', { enabled: !enabled });
            setEnabled(res.data.enabled);
            toast.success(res.data.enabled ? 'Appel aux commandes activé' : 'Appel aux commandes désactivé');
        } catch { toast.error('Erreur'); }
    };

    const toggleProduct = (product) => {
        setSelectedProducts(prev => prev.find(p => p.id === product.id)
            ? prev.filter(p => p.id !== product.id) : [...prev, product]);
    };

    const toggleClient = (client) => {
        setSelectedClients(prev => prev.find(c => c.id === client.id)
            ? prev.filter(c => c.id !== client.id) : [...prev, client]);
    };

    const filteredProducts = products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()));
    const filteredClients = clients.filter(c =>
        (c.company_name || '').toLowerCase().includes(clientSearch.toLowerCase()) ||
        (c.contact_name || '').toLowerCase().includes(clientSearch.toLowerCase())
    );

    const selectAllClients = () => {
        const allSelected = filteredClients.every(c => selectedClients.find(sc => sc.id === c.id));
        if (allSelected) {
            const ids = new Set(filteredClients.map(c => c.id));
            setSelectedClients(prev => prev.filter(c => !ids.has(c.id)));
        } else {
            setSelectedClients(prev => {
                const existing = new Set(prev.map(c => c.id));
                return [...prev, ...filteredClients.filter(c => !existing.has(c.id))];
            });
        }
    };

    const selectAllProducts = () => {
        const allSelected = filteredProducts.every(p => selectedProducts.find(sp => sp.id === p.id));
        if (allSelected) {
            const ids = new Set(filteredProducts.map(p => p.id));
            setSelectedProducts(prev => prev.filter(p => !ids.has(p.id)));
        } else {
            setSelectedProducts(prev => {
                const existing = new Set(prev.map(p => p.id));
                return [...prev, ...filteredProducts.filter(p => !existing.has(p.id))];
            });
        }
    };

    const handleImportCSV = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true); setImportResult(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post('/admin/import/supplier-products', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setImportResult(res.data);
            toast.success(res.data.message || 'Import terminé');
            await loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de l\'import');
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSend = async () => {
        if (!selectedProducts.length || !selectedClients.length || !deadline) {
            toast.error('Sélectionnez des produits, des clients et une date limite'); return;
        }
        setSending(true);
        try {
            const res = await api.post('/admin/order-calls', {
                product_ids: selectedProducts.map(p => p.id),
                client_ids: selectedClients.map(c => c.id),
                deadline: `${deadline} à ${deadlineTime}`, message, send_email: sendEmail,
            });
            toast.success(`Appel envoyé — ${res.data.emails_sent} emails, ${res.data.clients_notified} notifications`);
            setSelectedProducts([]); setSelectedClients([]); setDeadline(''); setMessage('');
            loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally { setSending(false); }
    };

    const deleteCall = async (callId) => {
        if (!window.confirm('Supprimer cet appel ?')) return;
        try { await api.delete(`/admin/order-calls/${callId}`); toast.success('Appel supprimé'); loadHistory(); }
        catch { toast.error('Erreur'); }
    };

    if (loading) {
        return (
            <div className="p-6 space-y-4">
                {[1, 2, 3].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-2xl" />)}
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6" data-testid="order-calls-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Megaphone className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Appel aux Commandes</h1>
                        <p className="text-sm text-gray-500">Envoyez un appel aux clients pour passer commande</p>
                    </div>
                </div>
                <button onClick={toggleEnabled}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                    data-testid="toggle-order-calls">
                    {enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    {enabled ? 'Activé' : 'Désactivé'}
                </button>
            </div>

            {!enabled ? (
                <Card className="rounded-2xl border-dashed border-2 border-gray-200">
                    <CardContent className="py-12 text-center text-gray-400">
                        <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-40" />
                        <p className="text-lg font-medium">Fonctionnalité désactivée</p>
                        <p className="text-sm mt-1">Activez pour envoyer des appels aux commandes</p>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Import Supplier CSV */}
                    <Card className="rounded-2xl shadow-sm border-emerald-200 bg-emerald-50/30" data-testid="supplier-import-section">
                        <CardContent className="py-4 px-5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                                        <FileSpreadsheet className="w-4.5 h-4.5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm text-gray-900">Import Fournisseur</p>
                                        <p className="text-xs text-gray-500">Importez le CSV fournisseur pour mettre à jour les produits disponibles</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    {importResult && (
                                        <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-100 px-3 py-1.5 rounded-lg" data-testid="import-result">
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            {importResult.imported} créé(s), {importResult.updated} mis à jour
                                        </div>
                                    )}
                                    <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImportCSV} className="hidden" data-testid="supplier-csv-input" />
                                    <Button onClick={() => fileInputRef.current?.click()} disabled={importing} size="sm"
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs h-8 px-3" data-testid="import-csv-btn">
                                        {importing ? (
                                            <span className="flex items-center gap-1.5">
                                                <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                                                Import...
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" />Importer CSV</span>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* New Order Call Form */}
                    <Card className="rounded-2xl shadow-sm" data-testid="order-call-form">
                        <CardHeader><CardTitle className="text-lg">Nouvel Appel</CardTitle></CardHeader>
                        <CardContent className="space-y-5">
                            <ProductSelector
                                products={products}
                                selectedProducts={selectedProducts}
                                productSearch={productSearch}
                                onSearchChange={setProductSearch}
                                onToggle={toggleProduct}
                                onSelectAll={selectAllProducts}
                                showList={showProductList}
                                onToggleList={() => setShowProductList(v => !v)}
                            />

                            <ClientSelector
                                clients={clients}
                                selectedClients={selectedClients}
                                clientSearch={clientSearch}
                                onSearchChange={setClientSearch}
                                onToggle={toggleClient}
                                onSelectAll={selectAllClients}
                                showList={showClientList}
                                onToggleList={() => setShowClientList(v => !v)}
                            />

                            {/* Deadline & Message */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="flex items-center gap-2 mb-2">
                                        <Clock className="w-4 h-4 text-amber-600" />Date limite
                                    </Label>
                                    <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="rounded-xl" data-testid="deadline-date" />
                                </div>
                                <div>
                                    <Label className="mb-2 block">Heure limite</Label>
                                    <Input type="time" value={deadlineTime} onChange={e => setDeadlineTime(e.target.value)} className="rounded-xl" data-testid="deadline-time" />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-2 block">Message personnalisé (optionnel)</Label>
                                <textarea value={message} onChange={e => setMessage(e.target.value)}
                                    placeholder="Ex: Nouvelle arrivée de produits frais ce matin..."
                                    className="w-full border rounded-xl p-3 text-sm resize-none h-20 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    data-testid="custom-message" />
                            </div>

                            <div className="flex items-center gap-4 pt-2">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="rounded" />
                                    <Mail className="w-4 h-4 text-blue-500" />Email
                                </label>
                                <div className="flex items-center gap-2 text-sm text-amber-600">
                                    <Bell className="w-4 h-4" />Notification temps réel
                                </div>
                            </div>

                            <Button onClick={handleSend} disabled={sending || !selectedProducts.length || !selectedClients.length || !deadline}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-xl h-11 text-base" data-testid="send-order-call">
                                {sending ? (
                                    <span className="flex items-center gap-2">
                                        <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Envoi en cours...
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2"><Send className="w-4 h-4" />Envoyer l'appel aux commandes</span>
                                )}
                            </Button>
                        </CardContent>
                    </Card>

                    <OrderCallHistory
                        history={history}
                        expandedCall={expandedCall}
                        onToggleExpand={(id) => setExpandedCall(expandedCall === id ? null : id)}
                        onDelete={deleteCall}
                    />
                </>
            )}
        </div>
    );
}
